import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth.service';
import { TranslatePipe } from '@shared/partner-ui/pipes/translate.pipe';
import { LanguageMenuComponent } from './components/language-menu/language-menu.component';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';
import { FeatureFlagsService } from './services/feature-flags.service';
import { TrackClickDirective } from './directives/track-click.directive';
import { environment } from '../environments/environment';
import { LanguageService } from '@shared/partner-ui/services/language.service';
import { AnalyticsService } from './services/analytics.service';
import { ToastService } from './services/toast.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule,
    TranslatePipe,
    LanguageMenuComponent,
    ToastContainerComponent,
    TrackClickDirective
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly auth = inject(AuthService);
  protected readonly featureFlags = inject(FeatureFlagsService);
  protected readonly language = inject(LanguageService);
  protected readonly loggingOut = signal(false);
  protected readonly userPortalUrlBase = environment.userPortalUrl.replace(/\/$/, '');
  protected readonly impersonation = this.auth.impersonation;
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  private readonly toast = inject(ToastService);

  constructor() {
    void this.analytics;
    void this.featureFlags.loadFlags();
  }

  protected async logout(): Promise<void> {
    this.loggingOut.set(true);
    try {
      await this.auth.logout();
      await this.router.navigate(['/', this.language.currentLanguage, 'welcome']);
    } finally {
      this.loggingOut.set(false);
    }
  }

  protected get userPortalMapUrl(): string {
    return `${this.userPortalUrlBase}/${this.language.currentLanguage}/map`;
  }

  protected async stopPreview(): Promise<void> {
    this.loggingOut.set(true);
    try {
      await this.auth.stopImpersonation();
      this.toast.success(this.language.translate('impersonation.messages.stopped'));
      await this.router.navigate(['/', this.language.currentLanguage, 'welcome']);
    } finally {
      this.loggingOut.set(false);
    }
  }

  protected showAppHeader(): boolean {
    return !/^\/[^/]+\/(?:experiences|tours)\/[^/]+\/bookings\/\d{4}-\d{2}-\d{2}\/print(?:[/?#]|$)/.test(this.router.url);
  }
}
